import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Box, Text } from '@chakra-ui/react';

interface MarkdownRendererProps {
  content: string;
}

/**
 * Renderiza texto com suporte a menções @[Nome](id:123) e markdown.
 * Menções são exibidas como badges azuis inline.
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  // Pré-processar menções: @[Nome](id:123) → badge
  const renderContent = (text: string) => {
    const mentionRegex = /@\[([^\]]+)\]\(id:\d+\)/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null = mentionRegex.exec(text);

    while (match !== null) {
      if (match.index > lastIndex) {
        parts.push(
          <ReactMarkdown key={`md-${lastIndex}`}>
            {text.slice(lastIndex, match.index)}
          </ReactMarkdown>,
        );
      }
      parts.push(
        <Text
          as="span"
          key={`mention-${match.index}`}
          color="blue.600"
          fontWeight="600"
          fontSize="inherit"
        >
          @{match[1]}
        </Text>,
      );
      lastIndex = match.index + match[0].length;
      match = mentionRegex.exec(text);
    }

    if (lastIndex < text.length) {
      parts.push(<ReactMarkdown key={`md-${lastIndex}`}>{text.slice(lastIndex)}</ReactMarkdown>);
    }

    return parts.length > 0 ? parts : <ReactMarkdown>{text || ''}</ReactMarkdown>;
  };

  return (
    <Box
      className="markdown-content"
      sx={{
        '& p': {
          marginBottom: '0.5rem',
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          fontWeight: 'bold',
          marginTop: '1rem',
          marginBottom: '0.5rem',
        },
        '& h1': { fontSize: '1.5rem' },
        '& h2': { fontSize: '1.25rem' },
        '& h3': { fontSize: '1.1rem' },
        '& ul, & ol': {
          paddingLeft: '1.5rem',
          marginBottom: '0.5rem',
        },
        '& li': {
          marginBottom: '0.25rem',
        },
        '& code': {
          backgroundColor: 'gray.100',
          padding: '0.125rem 0.25rem',
          borderRadius: '4px',
          fontSize: '0.875rem',
          fontFamily: 'monospace',
        },
        '& pre': {
          backgroundColor: 'gray.100',
          padding: '0.75rem',
          borderRadius: '6px',
          overflow: 'auto',
          marginBottom: '0.5rem',
        },
        '& pre code': {
          backgroundColor: 'transparent',
          padding: 0,
        },
        '& blockquote': {
          borderLeft: '3px solid',
          borderColor: 'gray.300',
          paddingLeft: '1rem',
          marginLeft: 0,
          color: 'gray.600',
          fontStyle: 'italic',
        },
        '& a': {
          color: 'blue.500',
          textDecoration: 'underline',
        },
        '& hr': {
          marginY: '1rem',
          borderColor: 'gray.200',
        },
        '& table': {
          width: '100%',
          borderCollapse: 'collapse',
          marginBottom: '0.5rem',
        },
        '& th, & td': {
          border: '1px solid',
          borderColor: 'gray.200',
          padding: '0.5rem',
        },
        '& th': {
          backgroundColor: 'gray.50',
          fontWeight: 'bold',
        },
      }}
    >
      {renderContent(content || '')}
    </Box>
  );
};
